<template>
  <div class="amprod-slot-options">
    <div class="slot-header">
      <h3>{{ getSlotName() }}</h3>
      <div class="slot-selection-info">
        <span class="selection-count">
          {{ selectionCount }} selected
          <span v-if="getTotalUsage > selectionCount">
            ({{ getTotalUsage }} total)
          </span>
        </span>
        <span v-if="maxSelections !== null" class="selection-limit">
          (max {{ maxSelections }})
        </span>
        <span v-if="minSelections > 0" class="selection-minimum">
          (min {{ minSelections }})
        </span>
      </div>
    </div>

    <p v-if="slotData.description">
      {{ slotData.description }}
    </p>

    <div
      v-if="validationMessage"
      class="validation-message"
      :class="{ error: !hasMinimumSelections, warning: hasMinimumSelections }"
    >
      {{ validationMessage }}
    </div>

    <div class="amprod-options-grid">
      <option-card
        v-for="option in slotData.options"
        :key="option.id"
        :option="option"
        :selected="isSelected(option)"
        :disabled="
          !isCompatible(option) || (!isSelected(option) && !canSelectMore)
        "
        :incompatibility-reason="getIncompatibilityReason(option)"
        :allow-quantity="allowQuantityForOption(option)"
        :quantity="getOptionQuantity(option)"
        :max-quantity="getMaxQuantityForOption(option)"
        @select="selectOption(option)"
        @quantity-change="handleQuantityChange"
      />
    </div>

    <p
      v-if="!slotData.options || slotData.options.length === 0"
      class="no-options"
    >
      No options available for this configuration slot.
    </p>
  </div>
</template>

<script>
import OptionCard from "./OptionCard.vue";
import { checkOptionCompatibility } from "../utils/compatibilityRules.js";

export default {
  name: "SlotOptions",
  components: {
    OptionCard,
  },
  props: {
    slotData: {
      type: Object,
      required: true,
    },
    configuration: {
      type: Object,
      required: true,
    },
    compatibilityRules: {
      type: Array,
      default: () => [],
    },
    configurableSlots: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["option-selected", "quantity-changed"],
  computed: {
    slotId() {
      return this.getSlotId();
    },
    currentSlotSelections() {
      // Get all selections for this specific slot
      const slotId = this.getSlotId();
      const slotConfig = this.configuration[slotId];

      // Handle both single selection and multiple selections
      if (!slotConfig) return [];
      if (Array.isArray(slotConfig)) return slotConfig;
      return [slotConfig];
    },
    selectionCount() {
      return this.currentSlotSelections.length;
    },
    getTotalUsage() {
      // Calculate total usage considering quantities
      return this.currentSlotSelections.reduce(
        (total, selected) => total + (selected.quantity || 1),
        0
      );
    },
    minSelections() {
      return this.slotData.min_selections || 0;
    },
    maxSelections() {
      return this.slotData.max_selections || null;
    },
    canSelectMore() {
      if (this.maxSelections === null) return true;

      // Calculate current total usage (considering quantities)
      const currentUsage = this.currentSlotSelections.reduce(
        (total, selected) => total + (selected.quantity || 1),
        0
      );

      return currentUsage < this.maxSelections;
    },
    hasMinimumSelections() {
      return this.selectionCount >= this.minSelections;
    },
    validationMessage() {
      if (!this.hasMinimumSelections) {
        return `Please select at least ${this.minSelections} option(s)`;
      }
      if (this.maxSelections !== null) {
        const currentUsage = this.currentSlotSelections.reduce(
          (total, selected) => total + (selected.quantity || 1),
          0
        );

        if (currentUsage >= this.maxSelections) {
          return `Maximum ${this.maxSelections} selection(s) reached`;
        }
      }
      return null;
    },
  },
  methods: {
    getSlotName() {
      // More defensive handling since slots are optional groupings
      if (!this.slotData) return "Options";
      return (
        this.slotData.name ||
        (this.slotData.type && this.slotData.type.name) ||
        "Options"
      );
    },

    isSelected(option) {
      return this.currentSlotSelections.some(
        (selected) => selected.id === option.id
      );
    },

    getSlotId() {
      // More defensive handling for slot ID generation
      if (!this.slotData) return "default";
      if (this.slotData.type && this.slotData.type.code) {
        return this.slotData.type.code;
      }
      if (this.slotData.id) {
        return this.slotData.id;
      }
      return "default";
    },

    selectOption(option) {
      if (!this.isCompatible(option)) {
        return; // Don't select incompatible options
      }

      const slotId = this.getSlotId();
      const isCurrentlySelected = this.isSelected(option);

      // If option is already selected, deselect it (toggle behavior)
      if (isCurrentlySelected) {
        this.$emit("option-selected", slotId, option, "deselect");
        return;
      }

      // If we can't select more options, don't proceed
      if (!this.canSelectMore) {
        return;
      }

      // Select the option
      this.$emit("option-selected", slotId, option, "select");
    },

    isCompatible(option) {
      // Check if already selected - always compatible
      if (this.isSelected(option)) {
        return true;
      }

      // Use the utility function for compatibility checking
      const result = checkOptionCompatibility(
        option,
        this.configuration,
        this.compatibilityRules
      );

      return result.compatible;
    },

    getIncompatibilityReason(option) {
      // Check selection limits first
      if (!this.isSelected(option) && !this.canSelectMore) {
        return `Maximum ${this.maxSelections} option(s) already selected for this slot`;
      }

      // If option is already selected, no incompatibility reason
      if (this.isSelected(option)) {
        return null;
      }

      // Use the utility function for compatibility checking
      const result = checkOptionCompatibility(
        option,
        this.configuration,
        this.compatibilityRules
      );

      return result.reason;
    },

    allowQuantityForOption(option) {
      // Allow quantity controls only if:
      // 1. The option is selected
      // 2. The slot allows multiple selections (max > 1 or unlimited)
      return (
        this.isSelected(option) &&
        (this.maxSelections === null || this.maxSelections > 1)
      );
    },

    getOptionQuantity(option) {
      // Find the option in the current slot selections and return its quantity
      const selectedOption = this.currentSlotSelections.find(
        (selected) => selected.id === option.id
      );
      return selectedOption?.quantity || 1;
    },

    getMaxQuantityForOption(option) {
      // If no max selections limit, return null (unlimited)
      if (this.maxSelections === null) {
        return null;
      }

      // If option is not selected, no max quantity applies
      if (!this.isSelected(option)) {
        return null;
      }

      // Calculate current usage by other selected options (considering their quantities)
      const otherUsage = this.currentSlotSelections
        .filter((selected) => selected.id !== option.id)
        .reduce((total, selected) => total + (selected.quantity || 1), 0);

      // Maximum quantity for this option is the remaining slots
      return Math.max(1, this.maxSelections - otherUsage);
    },

    handleQuantityChange(option, newQuantity) {
      const slotId = this.getSlotId();
      this.$emit("quantity-changed", slotId, option, newQuantity);
    },
  },
};
</script>

<style scoped>
.amprod-slot-options {
  padding: 15px;
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.slot-header h3 {
  margin: 0;
}

.slot-selection-info {
  font-size: 14px;
  color: #666;
}

.selection-count {
  font-weight: 600;
  color: #0073aa;
}

.selection-limit,
.selection-minimum {
  margin-left: 8px;
  color: #999;
}

.validation-message {
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
  font-weight: 500;
}

.validation-message.error {
  background-color: #ffeaea;
  color: #d32f2f;
  border: 1px solid #ffcdd2;
}

.validation-message.warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.amprod-options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
  margin-top: 20px;
}

.no-options {
  font-style: italic;
  color: #666;
  margin: 20px 0;
  text-align: center;
}

@media (max-width: 768px) {
  .slot-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .slot-selection-info {
    font-size: 12px;
  }
}
</style>
